import React from "react";
import { Link } from "react-router-dom";
import aiImage from "../../../assets/SEO.jpg";
import SEOComponent from "../../SEOComponent";
import OptimizedImage from "../../OptimizedImage";
import strategyImage from "../../../assets/SEO-2.jpg";
import seo3 from "../../../assets/SEO3.webp";
import seo4 from "../../../assets/seo4.webp";
import seo5 from "../../../assets/SEO5.avif";
import seo6 from "../../../assets/SEO6.webp";
import seo7 from "../../../assets/SEO7.webp";
import seo8 from "../../../assets/SEO8.webp";
import seo9 from "../../../assets/SEO9.webp";
import seo10 from "../../../assets/SEO10.webp";

export default function SEO() {
  return (
    <div className="bg-gradient-to-br from-black via-gray-900 to-black">
      <SEOComponent 
        title="SEO Services - Search Engine Optimization & Ranking | AlifShams"
        description="Professional SEO services to boost your search rankings. Technical SEO audits, keyword research, on-page optimization, local SEO, and performance analytics to drive organic traffic."
        keywords="SEO services, search engine optimization, keyword research, technical SEO, on-page SEO, local SEO, SEO audit, organic traffic, search rankings, SEO strategy"
        url="https://digitalif.net/solutions/digital-marketing/seo"
        type="service"
      />
      {/* Hero Section */}
      <section className="relative h-[60vh] sm:h-[70vh] md:h-[800px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src={aiImage}
            alt="SEO"
            className="w-full h-full object-cover sm:object-contain md:object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
            SEO
          </h1>
          <p className="text-sm sm:text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed">
            Enhance organic visibility through technical SEO, keyword strategy, and performance insights
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
            {/* Image - Left Side */}
            <div className="flex-1 w-full">
              <OptimizedImage
                src={strategyImage}
                alt="SEO"
                className="w-full h-64 sm:h-80 md:h-96 object-cover rounded-lg shadow-lg"
              />
            </div>

            {/* Content - Right Side */}
            <div className="flex-1 w-full">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 text-white text-center lg:text-left">
                SEO
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed mb-4 sm:mb-6 text-center lg:text-left">
                Enhance organic visibility through technical SEO, keyword strategy, and performance insights.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed text-center lg:text-left">
                Our comprehensive SEO approach improves your search rankings and drives qualified organic traffic to boost your online presence and conversions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Process Section */}
      <section className="py-16 px-4 sm:px-6 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
            SEO Process Explained
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* SEO Immersion Sessions */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-2 border-blue-500 mx-auto mb-4 transition-colors duration-300 overflow-hidden">
                <OptimizedImage
                  src={seo3}
                  alt="Immersion Session"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold mb-3 text-[#B31F7E]">
                Immersion Session
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                One on one session with the organisation and the ALIF team to help understand the business better - objectives and goals
              </p>
            </div>

            {/* SEO and Keywords */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-2 border-blue-500 mx-auto mb-4 transition-colors duration-300 overflow-hidden">
                <OptimizedImage
                  src={seo4}
                  alt="SEO and Keywords"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold mb-3 text-[#B31F7E]">
                SEO and Keywords
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Designing content with search engine in mind - adding keywords and potential topics to the list of searches to bring in traffic
              </p>
            </div>

            {/* Content Gap Analysis */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-2 border-blue-500 mx-auto mb-4 transition-colors duration-300 overflow-hidden">
                <OptimizedImage
                  src={seo5}
                  alt="Content Gap Analysis"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold mb-3 text-[#B31F7E]">
                Content Gap Analysis
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Audit on the current content by the team and analysing the gaps and optimizing it with SEO team
              </p>
            </div>

            {/* Content Clusters */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-2 border-blue-500 mx-auto mb-4 transition-colors duration-300 overflow-hidden">
                <OptimizedImage
                  src={seo6}
                  alt="Content Clusters"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold mb-3 text-[#B31F7E]">
                Content Clusters
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Creation of new content clusters, repurposed content and pillar pages focusing on specific keywords
              </p>
            </div>

            {/* Quarterly Plan */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-2 border-blue-500 mx-auto mb-4 transition-colors duration-300 overflow-hidden">
                <OptimizedImage
                  src={seo7}
                  alt="Quarterly Plan"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold mb-3 text-[#B31F7E]">
                Quarterly Plan
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Creation of quarterly plan based on objectives - to include how to target the B2B sector with already existing content and new content
              </p>
            </div>

            {/* Content Workshop */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-2 border-blue-500 mx-auto mb-4 transition-colors duration-300 overflow-hidden">
                <OptimizedImage
                  src={seo8}
                  alt="Content Workshop"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold mb-3 text-[#B31F7E]">
                Content Workshop
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Explaining overarching content strategy, how we will use existing content and also create new content to reach our target market
              </p>
            </div>

            {/* Production */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-2 border-blue-500 mx-auto mb-4 transition-colors duration-300 overflow-hidden">
                <OptimizedImage
                  src={seo9}
                  alt="Production"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold mb-3 text-[#B31F7E]">
                Production
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Developing new blogs, sharing repurposed content along with content suggestions - infographic, videos, animations, podcast etc
              </p>
            </div>

            {/* Reporting */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full border-2 border-blue-500 mx-auto mb-4 transition-colors duration-300 overflow-hidden">
                <OptimizedImage
                  src={seo10}
                  alt="Reporting"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold mb-3 text-[#B31F7E]">
                Reporting
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Analysis to see what content is driving the most organic traffic which drives the content plan for month 3–6 in line with goals
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Services Section */}
      <section className="py-16 px-4 sm:px-6 bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
            Our Search Engine Optimization / SEO Agency Services
          </h2>
          
          <div className="flex flex-wrap justify-center gap-8">
            {/* Website and SEO Audits */}
            <div className="bg-black border border-gray-600 rounded-lg shadow-lg p-6 hover:bg-gray-900 transition-colors duration-300 flex flex-col w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-xl font-bold mb-4 text-[#B31F7E]">
                Website and SEO Audits
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                A good place to start is often a SEO Audit of your existing website. Get your free instant audit here.
              </p>
              <Link 
                to="/contact" 
                className="inline-block bg-[#B31F7E] text-white px-4 py-2 rounded-lg hover:bg-[#9a1a6b] transition-colors duration-300 mt-auto"
              >
                Speak to Us
              </Link>
            </div>

            {/* Website Updates for SEO */}
            <div className="bg-black border border-gray-600 rounded-lg shadow-lg p-6 hover:bg-gray-900 transition-colors duration-300 flex flex-col w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-xl font-bold mb-4 text-[#B31F7E]">
                Website Updates for SEO
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                The structure of your website has a big influence on how your site ranks on the search engines. We can update most websites to ensure maximum search effectiveness.
              </p>
              <Link 
                to="/contact" 
                className="inline-block bg-[#B31F7E] text-white px-4 py-2 rounded-lg hover:bg-[#9a1a6b] transition-colors duration-300 mt-auto"
              >
                Speak to Us
              </Link>
            </div>

            {/* Website Backlink Building */}
            <div className="bg-black border border-gray-600 rounded-lg shadow-lg p-6 hover:bg-gray-900 transition-colors duration-300 flex flex-col w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-xl font-bold mb-4 text-[#B31F7E]">
                Website Backlink Building
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                A highly complex task, the building of backlinks is an important component of SEO but if executed incorrectly can cause your website to be penalised by Google.
              </p>
              <Link 
                to="/contact" 
                className="inline-block bg-[#B31F7E] text-white px-4 py-2 rounded-lg hover:bg-[#9a1a6b] transition-colors duration-300 mt-auto"
              >
                Speak to Us
              </Link>
            </div>

            {/* SEO Content Creation */}
            <div className="bg-black border border-gray-600 rounded-lg shadow-lg p-6 hover:bg-gray-900 transition-colors duration-300 flex flex-col w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-xl font-bold mb-4 text-[#B31F7E]">
                SEO Content Creation
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                Google expects your website to have high quality content in order to be able to rank it effectively. Our team creates content for websites that is SEO friendly and optimised for search.
              </p>
              <Link 
                to="/contact" 
                className="inline-block bg-[#B31F7E] text-white px-4 py-2 rounded-lg hover:bg-[#9a1a6b] transition-colors duration-300 mt-auto"
              >
                Speak to Us
              </Link>
            </div>

            {/* Google Whitelisting and Penalisation Recovery */}
            <div className="bg-black border border-gray-600 rounded-lg shadow-lg p-6 hover:bg-gray-900 transition-colors duration-300 flex flex-col w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-xl font-bold mb-4 text-[#B31F7E]">
                Google Whitelisting and Penalisation Recovery
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                If your website has been penalised by Google, our team can help rectify this and get your website ranking again.
              </p>
              <Link 
                to="/contact" 
                className="inline-block bg-[#B31F7E] text-white px-4 py-2 rounded-lg hover:bg-[#9a1a6b] transition-colors duration-300 mt-auto"
              >
                Speak to Us
              </Link>
            </div>

            {/* E-Book and E-Guide Design and Creation */}
            <div className="bg-black border border-gray-600 rounded-lg shadow-lg p-6 hover:bg-gray-900 transition-colors duration-300 flex flex-col w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-xl font-bold mb-4 text-[#B31F7E]">
                E-Book and E-Guide Design and Creation
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                E-Books and E-Guides are a wonderful way to educate potential customers and generate new, qualified sales leads.
              </p>
              <Link 
                to="/contact" 
                className="inline-block bg-[#B31F7E] text-white px-4 py-2 rounded-lg hover:bg-[#9a1a6b] transition-colors duration-300 mt-auto"
              >
                Speak to Us
              </Link>
            </div>

            {/* Blog Creation and Blog Article Creation */}
            <div className="bg-black border border-gray-600 rounded-lg shadow-lg p-6 hover:bg-gray-900 transition-colors duration-300 flex flex-col w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
              <h3 className="text-xl font-bold mb-4 text-[#B31F7E]">
                Blog Creation and Blog Article Creation
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                Hosting a blog on your website doesn't have to be a challenge. We create over 1,500 articles for our clients each year. Learn how you can start and drive more traffic to your website.
              </p>
              <Link 
                to="/contact" 
                className="inline-block bg-[#B31F7E] text-white px-4 py-2 rounded-lg hover:bg-[#9a1a6b] transition-colors duration-300 mt-auto"
              >
                Speak to Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}